import { useEffect, useState } from "react";
import { Share2, Download, Copy, Trophy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useVerse } from "@/lib/bible";

export const CARD_MILESTONES: Record<number, { title: string; verse: string; ref: string; colors: [string, string] }> = {
  7: { title: "A week with the Word", verse: "His compassions fail not. They are new every morning: great is thy faithfulness.", ref: "Lamentations 3:22-23", colors: ["#1E293B", "#C86D51"] },
  30: { title: "A month of light", verse: "Thy word is a lamp unto my feet, and a light unto my path.", ref: "Psalm 119:105", colors: ["#243B35", "#B7791F"] },
  100: { title: "One hundred days of faithfulness", verse: "They shall run, and not be weary; and they shall walk, and not faint.", ref: "Isaiah 40:31", colors: ["#0B1222", "#7C3AED"] },
};

export function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number) {
  const lines: string[] = [];
  let line = "";
  text.split(" ").forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  });
  return [...lines, line];
}

async function drawCard(m: number, username: string, verse: string, abbr: string) {
  const c = { ...CARD_MILESTONES[m], verse };
  await document.fonts.ready;
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d")!;
  const g = x.createLinearGradient(0, 0, W * 0.4, H);
  g.addColorStop(0, c.colors[0]); g.addColorStop(1, c.colors[1]);
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  // glow + water lines
  const glow = x.createRadialGradient(W * 0.75, H * 0.2, 10, W * 0.75, H * 0.2, 520);
  glow.addColorStop(0, "rgba(255,236,190,0.45)"); glow.addColorStop(1, "rgba(255,236,190,0)");
  x.fillStyle = glow; x.fillRect(0, 0, W, H);
  x.strokeStyle = "rgba(255,255,255,0.10)"; x.lineWidth = 3;
  for (let i = 0; i < 7; i++) {
    const y = H - 120 - i * 34;
    x.beginPath();
    for (let px = 0; px <= W; px += 20) x.lineTo(px, y + Math.sin(px / 70 + i) * 8);
    x.stroke();
  }
  // simple boat with sail
  x.fillStyle = "rgba(255,248,235,0.92)";
  x.beginPath(); x.moveTo(W - 330, H - 330); x.lineTo(W - 150, H - 330); x.lineTo(W - 185, H - 290); x.lineTo(W - 295, H - 290); x.closePath(); x.fill();
  x.fillRect(W - 243, H - 520, 6, 190);
  x.beginPath(); x.moveTo(W - 234, H - 515); x.lineTo(W - 150, H - 345); x.lineTo(W - 234, H - 345); x.closePath(); x.fill();
  // text
  x.fillStyle = "#FAF7F2";
  x.font = "700 64px 'Cormorant Garamond', serif";
  x.fillText("Cast", 90, 140);
  x.fillStyle = "#F5C98B";
  x.font = "600 26px Manrope, sans-serif";
  x.fillText("STREAK MILESTONE", 92, 190);
  x.fillStyle = "#FFFFFF";
  x.font = "700 300px 'Cormorant Garamond', serif";
  x.fillText(String(m), 80, 450);
  x.font = "600 64px 'Cormorant Garamond', serif";
  x.fillText(`day${m === 1 ? "" : "s"} in a row`, 90, 640);
  x.fillStyle = "#F5C98B";
  x.font = "600 44px 'Cormorant Garamond', serif";
  x.fillText(c.title, 90, 710);
  x.fillStyle = "rgba(255,255,255,0.95)";
  x.font = "italic 48px 'EB Garamond', serif";
  const lines = wrap(x, `“${c.verse}”`, W - 440);
  lines.forEach((l, i) => x.fillText(l, 90, 820 + i * 66));
  x.fillStyle = "#F5C98B";
  x.font = "600 30px Manrope, sans-serif";
  x.fillText(`${c.ref} · ${abbr}`, 92, 840 + lines.length * 66);
  x.fillStyle = "rgba(255,255,255,0.75)";
  x.font = "500 30px Manrope, sans-serif";
  x.fillText(`— ${username}`, 92, H - 90);
  return cv;
}

export function MilestoneCardDialog({ milestone, username, open, onOpenChange }: { milestone: number; username: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [url, setUrl] = useState("");
  const [blob, setBlob] = useState<Blob | null>(null);
  const c = CARD_MILESTONES[milestone];
  const v = useVerse(c?.ref || "", c?.verse || "");
  useEffect(() => {
    if (!open || !c) return;
    drawCard(milestone, username, v.text, v.abbr).then((cv) => { setUrl(cv.toDataURL("image/png")); cv.toBlob((b) => setBlob(b), "image/png"); });
  }, [open, milestone, username, c, v.text, v.abbr]);
  if (!c) return null;
  const text = `${milestone} days in a row with Scripture on Cast. “${v.text}” (${c.ref}, ${v.abbr})`;
  const file = () => new File([blob!], `cast-${milestone}-day-streak.png`, { type: "image/png" });
  const canShareFile = typeof navigator !== "undefined" && !!navigator.canShare && !!blob && navigator.canShare({ files: [file()] });
  const share = async () => {
    try {
      if (canShareFile) await navigator.share({ files: [file()], title: "My Cast streak", text });
      else if (navigator.share) await navigator.share({ title: "My Cast streak", text });
      else { await navigator.clipboard.writeText(text); toast.success("Copied to share"); }
    } catch (e: any) { if (e?.name !== "AbortError") toast.error("Sharing isn't available here. Try Download instead."); }
  };
  const download = () => { const a = document.createElement("a"); a.href = url; a.download = `cast-${milestone}-day-streak.png`; a.click(); };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Text copied"); } catch { toast.error("Couldn't copy"); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="milestone-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-3xl"><Trophy className="h-6 w-6 text-amber-500" />{milestone}-day milestone</DialogTitle>
          <DialogDescription>Share your card to encourage someone else to open the Word today.</DialogDescription>
        </DialogHeader>
        {url ? <img src={url} alt={`${milestone}-day streak card with ${c.ref}`} className="w-full rounded-2xl shadow-xl" data-testid="milestone-card-image" />
          : <div className="aspect-[4/5] w-full animate-pulse rounded-2xl bg-[#F3ECE0]" />}
        <div className="grid grid-cols-3 gap-2">
          <Button data-testid="milestone-share-button" onClick={share} disabled={!url} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]"><Share2 className="mr-1.5 h-4 w-4" />Share</Button>
          <Button data-testid="milestone-download-button" onClick={download} disabled={!url} variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Save</Button>
          <Button data-testid="milestone-copy-button" onClick={copy} variant="outline" className="rounded-full"><Copy className="mr-1.5 h-4 w-4" />Copy</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
